// import moment from 'moment';
import Swagger from 'swagger-client';
import _ from 'lodash';
import moment from 'moment-timezone';
import Queue from './Queue.js';

const SwaggerAPI = (function () {
  let refreshSession;
  let token;
  let swagger;
  let refreshVerifier;

  const getClientOpts = () => {
    return {
      requestInterceptor: (req) => {
        if (_.startsWith(req.url, 'https://api.teralytic.io')) {
          req.url = _.replace(req.url, 'https://api.teralytic.io', '/api');
          req.headers.Authorization = `Bearer ${token.access_token}`;
        }
        return req;
      },
    };
  };

  const getPublicClientOpts = () => {
    return {
      requestInterceptor: (req) => {
        if (_.startsWith(req.url, 'https://api.teralytic.io')) {
          req.url = _.replace(req.url, 'https://api.teralytic.io', '/api');
        }
        return req;
      },
    };
  };

  const initialize = (_token, _refreshSession, _refreshVerifier) => {
    refreshSession = _refreshSession;
    refreshVerifier = _refreshVerifier;
    token = _token;
    swagger = new Swagger('/api/1.0.0/swagger.json', getClientOpts());
  };

  const getSwaggerClient = async () => {
    try {
      if (moment().unix() > parseJwt(token.access_token).exp) {
        await Queue.enqueue(() => refreshSession(token, refreshVerifier));
      }
      return swagger;
    } catch (err) {
      if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
        console.log('Error with swagger', err);
      }
      throw err;
    }
  };

  const getPublicSwaggerClient = async () => {
    try {
      return new Swagger('/api/1.0.0/swagger.json', getPublicClientOpts());
    } catch (err) {
      if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
        console.log('Error with swagger', err);
      }
      throw err;
    }
  };
  const getCustomTokenSwaggerClient = async (access_token) => {
    if (token !== undefined) {
      token.access_token = access_token.ac;
    } else {
      token = {};
      token.access_token = access_token.ac;
    }

    try {
      return new Swagger('/api/1.0.0/swagger.json', getClientOpts());
    } catch (err) {
      if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
        console.log('Error with swagger', err);
      }
      throw err;
    }
  };

  const parseJwt = (token) => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => {
          return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`;
        })
        .join('')
    );

    return JSON.parse(jsonPayload);
  };

  const callAPI = async (apiCall, params, options) => {
    try {
      const client = await getSwaggerClient();
      const f = _.get(client, apiCall);
      const response = await f(params, options);
      if (response.status >= 200 && response.status < 300) {
        return response.body;
      }
      return response;
    } catch (err) {
      if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
        console.log('Error with API call', apiCall, params, err);
      }
      throw err;
    }
  };

  const callPublicAPI = async (apiCall, params, options) => {
    try {
      const client = await getPublicSwaggerClient();

      const f = _.get(client, apiCall);
      const response = await f(params, options);
      if (response.status >= 200 && response.status < 300) {
        return response.body;
      }
      return response;
    } catch (err) {
      if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
        console.log('Error with API call', apiCall, params, err);
      }
      throw err;
    }
  };

  const callCustomTokenAPI = async (apiCall, params, options, passedToken) => {
    try {
      const client = await getCustomTokenSwaggerClient(passedToken);
      const f = _.get(client, apiCall);

      const response = await f(params, options);

      if (response.status >= 200 && response.status < 300) {
        return response.body;
      }
      return response;
    } catch (err) {
      if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
        console.log('Error with API call', apiCall, params, err);
      }
      throw err;
    }
  };
  const getInstancePermissions = async () => {
    const client = await getSwaggerClient();
    return client?.spec?.securityDefinitions?.OAuth2?.scopes;
  };

  const getInstanceRoles = async () => {
    const client = await getSwaggerClient();
    return client?.spec?.securityDefinitions?.OAuth2?.roles;
  };

  const listUsers = (params) => {
    return callAPI('apis.users.userList', {
      team_id: params.team_id,
      skip: params.skip,
      limit: params.limit,
      organization_id: params.organization_id,
    });
  };

  const createUser = (user) => {
    return callAPI('apis.users.userCreate', {}, { requestBody: user });
  };

  const selectUser = (user_id) => {
    return callAPI('apis.users.userSelect', { user_id });
  };

  const updateUser = (user) => {
    return callAPI('apis.users.userUpdate', { user_id: user.user_id }, { requestBody: user });
  };

  const deleteUser = (user_id) => {
    return callAPI('apis.users.userDelete', { user_id });
  };

  const userCompleteRegistration = (user, passedToken) => {
    return callCustomTokenAPI('apis.users.userRegister', undefined, { requestBody: user }, passedToken);
  };

  const userRegistrationInvite = (email, clientId, user) => {
    return callAPI('apis.users.userRegistrationInvite', { email, client_id: clientId }, { requestBody: user });
  };

  const userRequestPasswordReset = (email, clientId) => {
    return callPublicAPI('apis.users.userRequestPasswordReset', { email: email, client_id: clientId });
  };
  const userResetPassword = (passwordObj, passedToken) => {
    return callCustomTokenAPI('apis.users.userPasswordReset', undefined, { requestBody: passwordObj }, passedToken);
  };

  const modelList = async () => {
    return callAPI('apis.devices.modelList');
  };

  const deviceCreate = (device) => {
    return callAPI('apis.devices.deviceCreate', {}, { requestBody: device });
  };

  const devicesUpdate = (params) => {
    return callAPI('apis.devices.devicesUpdate', {}, { requestBody: params });
  };

  const deviceList = async (organizationId) => {
    return callAPI('apis.devices.deviceList', { organization_id: organizationId });
  };

  const fieldCreate = (propertyId, field) => {
    return callAPI('apis.fields.fieldCreate', { property_id: propertyId }, { requestBody: field });
  };

  const fieldGet = (fieldId) => {
    return callAPI('apis.fields.fieldGet', { field_id: fieldId });
  };

  const fieldUpdate = (field) => {
    return callAPI('apis.fields.fieldUpdate', { field_id: field.id }, { requestBody: field });
  };

  const fieldDelete = (fieldId) => {
    return callAPI('apis.fields.fieldDeactivate', { field_id: fieldId });
  };

  const fieldList = (propertyId, organizationId) => {
    return callAPI('apis.fields.fieldList', { property_id: propertyId, organization_id: organizationId });
  };

  const propertyCreate = (property) => {
    return callAPI('apis.properties.propertyCreate', {}, { requestBody: property });
  };

  const propertyUpdate = (property) => {
    return callAPI('apis.properties.propertyUpdate', { property_id: property.id }, { requestBody: property });
  };

  const propertyList = (organizationId, userId) => {
    return callAPI('apis.properties.propertyList', { organization_id: organizationId, user_id: userId });
  };

  const propertyDelete = (property) => {
    return callAPI('apis.properties.propertyDelete', { property_id: property.id });
  };

  const userPropertyDelete = (userId, propertyId) => {
    return callAPI('apis.userproperties.userPropertyDelete', { user_id: userId, property_id: propertyId });
  };

  const userPropertyCreate = (userProperty) => {
    return callAPI('apis.userproperties.userPropertyCreate', {}, { requestBody: userProperty });
  };

  const cropCreate = (crop) => {
    return callAPI('apis.crops.cropCreate', {}, { requestBody: crop });
  };

  const cropUpdate = (crop) => {
    return callAPI('apis.crops.cropUpdate', { crop_id: crop.id }, { requestBody: crop });
  };

  const cropGet = (crop_id) => {
    return callAPI('apis.crops.cropGet', { crop_id });
  };

  const cropDelete = (crop_id) => {
    return callAPI('apis.crops.cropDelete', { crop_id });
  };

  const cropList = () => {
    return callAPI('apis.crops.cropList');
  };

  const healthIntervalCreate = (healthInterval) => {
    return callAPI('apis.healthIntervals.healthIntervalCreate', { healthInterval });
  };

  const healthIntervalUpdate = (healthInterval) => {
    return callAPI(
      'apis.healthIntervals.healthIntervalUpdate',
      { health_interval_id: healthInterval.id },
      { requestBody: healthInterval }
    );
  };

  const healthIntervalGet = (health_interval_id) => {
    return callAPI('apis.healthIntervals.healthIntervalGet', { health_interval_id });
  };

  const healthIntervalDelete = (health_interval_id) => {
    return callAPI('apis.healthIntervals.healthIntervalDelete', { health_interval_id });
  };

  const healthIntervalList = () => {
    return callAPI('apis.healthIntervals.healthIntervalList');
  };

  const sensorsList = () => {
    return callAPI('apis.sensors.sensorList');
  };

  const suggestionCreate = (suggestion) => {
    return callAPI('apis.suggestions.suggestionCreate', {}, { requestBody: suggestion });
  };

  const suggestionUpdate = (suggestion) => {
    return callAPI('apis.suggestions.suggestionUpdate', { suggestion_id: suggestion.id }, { requestBody: suggestion });
  };

  const suggestionGet = (suggestion_id) => {
    return callAPI('apis.suggestions.suggestionGet', { suggestion_id });
  };

  const suggestionDelete = (suggestion_id) => {
    return callAPI('apis.suggestions.suggestionDelete', { suggestion_id });
  };

  const suggestionList = (suggestion_query = {}) => {
    return callAPI('apis.suggestions.suggestionList', {
      limit: suggestion_query.limit,
      skip: suggestion_query.skip,
      health_interval_id: suggestion_query.health_interval_id,
      suggestion_id: suggestion_query.suggestion_id,
      bound: suggestion_query.bound,
      search: suggestion_query.search,
    });
  };

  const getDeviceTelemetry = (layerIds, days, reading, bucketPeriod, bucketUnit, include_today) => {
    let d = new Date();
    let today_string = d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();

    return callAPI('apis.telemetry.telemetryAvgQuery', {
      field_id: layerIds.field_id,
      hw_address: layerIds.hw_address,
      interval_length: days,
      bucket_period: bucketPeriod,
      bucket_unit: bucketUnit,
      telemetry_type: reading,
      include_today: include_today,
      today_string: today_string,
    });
  };

  const organizationList = () => {
    return callAPI('apis.organizations.organizationList');
  };

  const organizationCreate = (organization) => {
    return callAPI('apis.organizations.organizationCreate', {}, { requestBody: organization });
  };

  const organizationGet = (organization_id) => {
    return callAPI('apis.organizations.organizationGet', { organization_id });
  };

  const organizationUpdate = (organization) => {
    return callAPI(
      'apis.organizations.organizationUpdate',
      { organization_id: organization.id },
      { requestBody: organization }
    );
  };

  const organizationDelete = (organization_id) => {
    return callAPI('apis.organizations.organizationDelete', { organization_id });
  };

  const eventCreate = (event) => {
    return callAPI('apis.events.eventCreate', {}, { requestBody: event });
  };

  const eventQuery = (params) => {
    let p = params ? params : {};
    return callAPI('apis.events.eventList', {
      event_id: p.event_id,
      layer: p.layer,
      type: p.type,
      time_period_start: p.time_start,
      time_period_end: p.time_end,
      search: p.search,
      layer_id: p.layer_id,
      organization_id: p.organization_id,
    });
  };

  const eventCountQuery = (params) => {
    return callAPI('apis.events.eventGetCount', {
      layer: params.layer,
      type: params.type,
      time_period_start: params.time_period_start,
      time_period_end: params.time_period_end,
      search: params.search,
      layer_id: params.layer_id,
      organization_id: params.organization_id,
    });
  };

  const eventUpdate = (params) => {
    return callAPI('apis.events.eventUpdate', { event_id: params.id }, { requestBody: params });
  };

  const eventBatchUpdate = (params) => {
    return callAPI('apis.events.eventBatchUpdate', {}, { requestBody: params });
  };

  const eventGet = (event_id) => {
    return callAPI('apis.events.eventGet', { event_id });
  };

  const eventDelete = (event_id) => {
    return callAPI('apis.events.eventDelete', { event_id });
  };

  return {
    initialize,

    getInstancePermissions,
    getInstanceRoles,

    deviceCreate,
    devicesUpdate,

    deviceList,

    eventCreate,
    eventGet,
    eventCountQuery,
    eventQuery,
    eventUpdate,
    eventBatchUpdate,
    eventDelete,

    listUsers,
    createUser,
    selectUser,
    updateUser,
    deleteUser,
    userRegistrationInvite,
    userCompleteRegistration,
    userRequestPasswordReset,
    userResetPassword,

    modelList,

    organizationList,
    organizationCreate,
    organizationGet,
    organizationUpdate,
    organizationDelete,

    propertyList,
    propertyCreate,
    propertyUpdate,
    propertyDelete,
    userPropertyDelete,
    userPropertyCreate,

    fieldCreate,
    fieldUpdate,
    fieldDelete,
    fieldList,
    fieldGet,

    getDeviceTelemetry,

    cropList,
    cropDelete,
    cropGet,
    cropUpdate,
    cropCreate,

    healthIntervalList,
    healthIntervalDelete,
    healthIntervalGet,
    healthIntervalUpdate,
    healthIntervalCreate,

    suggestionList,
    suggestionDelete,
    suggestionGet,
    suggestionUpdate,
    suggestionCreate,

    sensorsList,
  };
})();

export default SwaggerAPI;
